import App from "./App.tsx";
import { I18nextProvider } from "react-i18next";
import i18n from "@/lib/i18n";
import React from "react";
import { AgentIdProvider } from "@/context/AgentIdContext";
import { ErrorDialogProvider } from "@/context/ErrorDialogContext";
import { fetchBrokerByToken } from "@/api/simpego";
import { localizeFlatpickr } from "@/utils/flatpickrLocale";
import { mountFunnel } from "@/shadow/mountFunnel";
import { FunnelErrorFallback } from "@/components/FunnelErrorFallback";


import * as Sentry from "@sentry/react";
import { normalizeAgentId } from "@/utils/agentId";
import { hashQueryParams, readUrlParam } from "@/utils/urlParams";

// Only report errors whose stack frames come from our own scripts, so GTM, chatbot and browser-extension noise stays
// out of Sentry and the session health. Events without stack frames (and React's own `removeChild` crashes, whose
// frames live in our bundle) are not filtered, so real user impact still shows up.
const ownScriptUrls = (): (string | RegExp)[] => {
  const urls: (string | RegExp)[] = [/^https?:\/\/([\w-]+\.)*simpego\.ch\//i];
  const basePath = window.__env__.widgetBasePath;
  if (basePath) {
    try {
      urls.push(new URL(basePath, window.location.href).origin);
    } catch {
      // an unparsable base path just means we rely on the simpego.ch pattern
    }
  }
  return urls;
};

if ((window.__env__.sentryEnv !== "development")) {
  Sentry.init({
    dsn: window.__env__.sentryDsn,
    // Route events through our own domain so adblockers don't drop them
    tunnel: window.__env__.sentryTunnel,
    environment: window.__env__.sentryEnv,
    release: window.__env__.sentryRelease,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    allowUrls: ownScriptUrls(),
  });
}



declare global {
  interface Window {
    __simpego_widget__?: { locale?: string };
  }
}

const rootElement = document.getElementById("root-simpego-funnel")!;


const Root = () => {
  const [isI18nReady, setIsI18nReady] = React.useState(false);
  const [initialAgentProfile, setInitialAgentProfile] = React.useState<{ agentId: string | null; agentName: string | null; agentToken: string | null; agentEmails: string[]; isDirectAgent: boolean } | null>(null);

  React.useEffect(() => {
    let partnerName: string | null = null;

    const hashParams = Object.fromEntries(hashQueryParams());
    partnerName = hashParams['s'];
    console.log("main.tsx - Raw partnerName from URL hash param 's':", partnerName);
    const getCookie = (name: string) => {
      if (typeof document === "undefined") return null;
      const match = document.cookie.split("; ").find(row => row.startsWith(`${name}=`));
      return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
    };
    const setCookie = (name: string, value: string, maxAgeSeconds?: number) => {
      if (typeof document === "undefined") return;
      const maxAgePart = typeof maxAgeSeconds === "number" ? `; max-age=${maxAgeSeconds}` : "";
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/${maxAgePart}`;
    };
    const setInitialAgentFromSources = async () => {
      let determinedProfile: { agentId: string | null; agentName: string | null; agentToken: string | null; agentEmails: string[]; isDirectAgent: boolean } | null = null;
      const cookieAgentToken = getCookie("AGENT_TOKEN");
      const normalizedDirectAgentId = normalizeAgentId(readUrlParam("agent"));

      if (normalizedDirectAgentId) {
        determinedProfile = {
          agentId: normalizedDirectAgentId,
          agentName: null,
          agentToken: null,
          agentEmails: [],
          isDirectAgent: true,
        };
        console.log("main.tsx - Direct agent provided via URL:", determinedProfile);
      } else if (hashParams['agentToken']) {
        try {
          const broker = await fetchBrokerByToken(hashParams['agentToken']);
          determinedProfile = {
            agentId: broker.uid || null,
            agentName: broker.name || null,
            agentToken: broker.token || null,
            agentEmails: Array.isArray(broker.emailAddresses) ? broker.emailAddresses : [],
            isDirectAgent: false,
          };
          if (broker.token) {
            setCookie("AGENT_TOKEN", broker.token);
          }
          console.log("main.tsx - Broker fetched via token:", determinedProfile);
        } catch (e) {
          console.error("main.tsx - Failed to fetch broker by token:", e);
        }
      } else if (cookieAgentToken) {
        try {
          const broker = await fetchBrokerByToken(cookieAgentToken);
          determinedProfile = {
            agentId: broker.uid || null,
            agentName: broker.name || null,
            agentToken: broker.token || null,
            agentEmails: Array.isArray(broker.emailAddresses) ? broker.emailAddresses : [],
            isDirectAgent: false,
          };
          console.log("main.tsx - Broker fetched via cookie token:", determinedProfile);
        } catch (e) {
          console.error("main.tsx - Failed to fetch broker by cookie token:", e);
        }
      }

      setInitialAgentProfile(determinedProfile);
      console.log("main.tsx - Determined initial agent profile:", determinedProfile);
    };

    const loadWlpTranslations = async () => {
      await setInitialAgentFromSources();

      const supported = i18n.options.supportedLngs;
      const widgetLocale = typeof window !== "undefined" ? window.__simpego_widget__?.locale : null;
      let preferredLocale: string | null = null;

      if (widgetLocale && supported?.includes(widgetLocale)) {
        preferredLocale = widgetLocale;
      }

      if (!preferredLocale) {
        const hashPathSegments = window.location.hash.split('?')[0].split('/').filter(Boolean);
        if (hashPathSegments.length > 0 && supported?.includes(hashPathSegments[0])) {
          preferredLocale = hashPathSegments[0];
        }
      }

      if (partnerName) {
        try {
          const translationPath = `/${partnerName}-translations.json`;
          console.log("main.tsx - Attempting to fetch translations from:", translationPath);
          const response = await fetch(translationPath);
          if (response.ok) {
            const wlpTranslations = await response.json();
            for (const langKey in wlpTranslations) {
              if (Object.prototype.hasOwnProperty.call(wlpTranslations, langKey)) {
                i18n.addResourceBundle(langKey, 'translation', wlpTranslations[langKey], true, true);
              }
            }
            console.log(`WLP translations for '${partnerName}' loaded and applied.`);
          } else {
            console.warn(`WLP translations file for '${partnerName}' not found or failed to load. Using base translations.`);
          }
        } catch (error) {
          console.error(`Error loading WLP translations for '${partnerName}':`, error);
        }
      }

      if (preferredLocale && i18n.language !== preferredLocale) {
        await i18n.changeLanguage(preferredLocale);
      }
      localizeFlatpickr(i18n.language);
      i18n.on("languageChanged", localizeFlatpickr);
      setIsI18nReady(true);

      const currentHash = window.location.hash;
      const [hashPath, hashQuery] = currentHash.split('?');
      const hashParamsObj = new URLSearchParams(hashQuery);

      let updatedHash = false;
      if (hashParamsObj.has('s')) {
        hashParamsObj.delete('s');
        updatedHash = true;
      }
      if (hashParamsObj.has('agentToken')) {
        hashParamsObj.delete('agentToken');
        updatedHash = true;
      }

      if (updatedHash) {
        const newHash = `${hashPath}${hashParamsObj.toString() ? `?${hashParamsObj.toString()}` : ''}`;
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}${newHash}`);
        console.log("main.tsx - Removed 's' and 'agentToken' parameters from URL hash.");
      }
    };

    loadWlpTranslations();

    return () => {
      i18n.off("languageChanged", localizeFlatpickr);
    };
  }, []);

  if (!isI18nReady) {
    return <div>Loading translations...</div>;
  }

  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <FunnelErrorFallback error={error} resetError={resetError} />}>
    <I18nextProvider i18n={i18n}>
      <AgentIdProvider initialAgentProfile={initialAgentProfile}>
        <ErrorDialogProvider>
          <App />
        </ErrorDialogProvider>
      </AgentIdProvider>
    </I18nextProvider>
    </Sentry.ErrorBoundary>
  );
};

mountFunnel(rootElement, <Root />);
